import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Search, TrendingUp, Target, BarChart, Zap, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useState } from "react";
import { SeoHead } from '@/components/SeoHead';

export default function SEOServices() {
  const { openDrawer } = useQuoteDrawer();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const services = [
    {
      title: "On-Page SEO",
      description: "Optimize your website content, meta tags, and structure for better search rankings.",
      icon: <Search className="h-8 w-8 text-blue-400" />
    },
    {
      title: "Technical SEO",
      description: "Improve site speed, mobile responsiveness, and technical search factors.",
      icon: <Target className="h-8 w-8 text-green-400" />
    },
    {
      title: "Content Strategy",
      description: "Create SEO-optimized content that attracts and engages your target audience.",
      icon: <TrendingUp className="h-8 w-8 text-purple-400" />
    },
    {
      title: "SEO Analytics",
      description: "Track rankings, traffic, and conversions with comprehensive SEO reporting.",
      icon: <BarChart className="h-8 w-8 text-yellow-400" />
    }
  ];

  return (
    <>
      <SeoHead 
        title="Professional SEO Services UAE | ZeeX Soft"
        description="Boost your search rankings in Dubai & Sharjah. Our data-driven SEO strategies deliver organic traffic growth."
        keywords="seo services uae, search engine optimization dubai, organic traffic sharjah"
      />

      <main className="bg-gray-900 text-white">
        <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Search Engine <span className="text-blue-600">Dominance</span>
            </h1>
            <p className="text-xl max-w-3xl mx-auto mb-8">
              Data-driven SEO strategies to grow your organic traffic
            </p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 py-24">
          <div className="container mx-auto px-6 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 mb-6">
                <Zap className="h-5 w-5 mr-2 text-white" />
                <span className="text-sm font-medium text-white">SEO Solutions</span>
              </div>
              <h1 className="font-orbitron font-bold text-5xl md:text-6xl text-white mb-6">
                Dominate <span className="gradient-text">Search Results</span>
              </h1>
              <p className="text-xl text-gray-300 mb-10">
                Improve your search rankings and drive organic traffic with our proven SEO strategies
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  onClick={openDrawer}
                >
                  Get SEO Audit
                </Button>
                <Link href="/portfolio">
                  <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
                    View Case Studies
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gray-800">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Our SEO Services</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">Comprehensive solutions to improve your search visibility</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-800 p-8 rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Process */}
        <section className="py-16 bg-gray-800">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Our SEO Process</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">Systematic approach to improve your search visibility</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: "01", title: "SEO Audit", description: "Comprehensive website and competitor analysis" },
                { step: "02", title: "Strategy", description: "Custom SEO strategy based on your goals" },
                { step: "03", title: "Implementation", description: "Execute on-page and technical optimizations" },
                { step: "04", title: "Reporting", description: "Track progress and adjust strategies" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="font-orbitron font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2 text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-gray-800">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Measurable Results</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">Our SEO strategies deliver consistent improvements</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { metric: "400%", label: "Organic Traffic Increase" },
                { metric: "75%", label: "Keywords in Top 10" },
                { metric: "250%", label: "Lead Generation" },
                { metric: "90%", label: "Client Retention" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800 text-white p-8 rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all text-center"
                >
                  <div className="font-orbitron font-bold text-4xl text-blue-400 mb-2">{stat.metric}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-white mb-6">Ready to Improve Your Search Rankings?</h2>
              <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
                Let's develop an SEO strategy that drives qualified traffic to your website
              </p>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 hover:text-blue-700 font-medium"
                onClick={openDrawer}
              >
                Get Started Today
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
